import { useEffect, useState, type FormEvent } from "react";
import QRCode from "qrcode";
import { api, ApiError } from "@/lib/api";
import type { LoginResult, Me } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/input";

/**
 * The 2FA step after a correct password (sign-in or a set-up link): enroll the authenticator
 * on first use (QR code + key), then a 6-digit code opens the session.
 */
export function TwoStepForm({ step, onDone, onExpired }: { step: LoginResult; onDone: (user: Me) => void; onExpired: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState("");

  useEffect(() => {
    if (step.otpauth_url) QRCode.toDataURL(step.otpauth_url, { margin: 1, width: 180 }).then(setQr, () => setQr(""));
  }, [step.otpauth_url]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api<{ user: Me }>("/api/v1/auth/totp", { json: { challenge: step.challenge, code } });
      onDone(r.user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That code didn't work.");
      if (err instanceof ApiError && err.message.includes("expired")) onExpired();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {step.status === "totp_setup_required" && (
        <div className="space-y-2 text-sm">
          <p>Set up two-step sign-in once: scan this with your authenticator app (1Password, Google Authenticator…), then enter the 6-digit code.</p>
          {qr && <img src={qr} alt="Authenticator QR code" className="mx-auto rounded border" width={180} height={180} />}
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Can't scan? Enter this key: <code className="break-all font-mono">{step.totp_secret}</code>
          </p>
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor="code">6-digit code</Label>
        <Input id="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" value={code} onChange={(e) => setCode(e.target.value)} autoFocus required />
      </div>
      <Button type="submit" className="w-full" disabled={busy}>
        Verify
      </Button>
      {error && (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {error}
        </p>
      )}
    </form>
  );
}
